import React, { useEffect, useState } from 'react'

import { useStripe } from '@stripe/react-stripe-js'

import { TGetItemFn, TSetItemFn } from '@/config/types'
import { PaymentMethod as PaymentMethodEnum } from '@/enums/payments'

type WalletTypeDetectorProps = {
  setItem: TSetItemFn
  getItem: TGetItemFn
  onWalletTypeChange: (type: PaymentMethodEnum | undefined) => void
  loadingTitle?: string
  children: React.ReactNode
}

export const WalletTypeDetector: React.FC<WalletTypeDetectorProps> = ({
  getItem,
  setItem,
  onWalletTypeChange,
  loadingTitle = 'Loading',
  children,
}) => {
  const stripe = useStripe()
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const storedWalletType = getItem('walletType') as PaymentMethodEnum | undefined

    if (storedWalletType) {
      onWalletTypeChange(storedWalletType)
      setLoading(false)
      return
    }

    if (stripe) {
      const pr = stripe.paymentRequest({
        country: 'US',
        currency: 'usd',
        total: {
          label: 'Demo total',
          amount: 1,
        },
        requestPayerName: true,
        requestPayerEmail: true,
      })

      const stripeRequest = pr
        .canMakePayment()
        .then((result) => {
          let detectedType: PaymentMethodEnum | undefined
          if (result?.googlePay) {
            detectedType = PaymentMethodEnum.GOOGLE_PAY
          } else if (result?.applePay) {
            detectedType = PaymentMethodEnum.APPLE_PAY
          }
          if (detectedType) {
            setItem('walletType', detectedType)
          }
          onWalletTypeChange(detectedType)
        })
        .finally(() => {
          setLoading(false)
        })

      const timerRequest = new Promise((resolve) => {
        setTimeout(() => resolve(undefined), 1000)
      })

      Promise.any([stripeRequest, timerRequest]).finally(() => {
        setLoading(false)
      })
    }
  }, [stripe, onWalletTypeChange, getItem, setItem])

  useEffect(() => {
    setTimeout(() => setLoading(false), 1500)
  }, [])

  if (loading) {
    return (
      <div className="wallet-detector-loading">
        <div className="loading-title">{loadingTitle}</div>
      </div>
    )
  }

  return <>{children}</>
}
